import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { isEmptyOrUndefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { useQueryClient } from "@tanstack/react-query";
import { App as AntApp, Card, Checkbox, Col, Flex, Form, Input, Modal, Popconfirm, Row, Segmented, Select } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCT_QUERY_KEYS,
  useCreateProduct,
  useUpdateProduct,
  useUpdateProductCommissionsSettings,
} from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { CreateUpdateEntries } from "../../../../common/types";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { ProductCommissionsSettingsForm } from "../../../commissions/productcommissionssettings/components/forms/ProductCommissionsSettingsForm";
import { CreateUpdateProductCommissionsSettings } from "../../../commissions/productcommissionssettings/types";
import { CommodityShape, InsuranceType } from "../../../contract/enums";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { LifeInsuranceTariffGroupSelect } from "../../../enumerations/components/form/LifeInsuranceTariffGroupSelect";
import ProductGroupSelect from "../../../enumerations/components/form/ProductGroupSelect";
import { ProductFinancialSector } from "../../enums";
import { CreateUpdateProduct, Product } from "../../types";
import {
  convertProductCommissionsSettingsToUpdateObject,
  convertProductToCreateUpdateObject,
  PRODUCT_SECTOR_TO_INSTITUTION_TYPE_MAP,
} from "../../utils";

interface Props {
  open: boolean;
  product?: Product;
  onFormCancel: () => void;
}

const colSpan = 8;

export const ProductModalForm = ({ open, product, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const queryClient = useQueryClient();
  const [isCoveredByNbsConfirmOpen, setIsCoveredByNbsConfirmOpen] = useState(false);

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const [productForm] = Form.useForm<CreateUpdateProduct>();
  const sector = Form.useWatch("sector", productForm);
  const insuranceType = Form.useWatch("insuranceType", productForm);
  const coveredByNbs = Form.useWatch("coveredByNbs", productForm);
  const ibisProductIds = Form.useWatch("ibisProductIds", productForm);

  const [productCommissionsSettingsForm] = Form.useForm<CreateUpdateEntries<CreateUpdateProductCommissionsSettings>>();

  const createProduct = useCreateProduct({ invalidateFilter: false });
  const updateProduct = useUpdateProduct({ invalidateFilter: false });

  const createUpdateProductCommissionsSettings = useUpdateProductCommissionsSettings();

  useEffect(() => {
    if (open && product) {
      const formData = convertProductToCreateUpdateObject(product);

      productForm.setFieldsValue(formData);
    }

    if (open && product?.commissionsSettings) {
      const formData = product.commissionsSettings.map((setting) =>
        convertProductCommissionsSettingsToUpdateObject(setting),
      );

      productCommissionsSettingsForm.setFieldsValue({ entries: formData });
    }
  }, [open, product, productForm, productCommissionsSettingsForm]);

  const handleSectorChange = () => {
    productForm.setFieldsValue({ institutionIds: [] });
  };

  const handleFormSubmit = async () => {
    const basePromise: Promise<CreateUpdateProduct> = productForm.validateFields();

    const createUpdateProductCommissionsSettingsPromise: Promise<
      CreateUpdateEntries<CreateUpdateProductCommissionsSettings>
    > = Promise.resolve({ entries: [] });

    const productFormsValidationPromises: [
      Promise<CreateUpdateProduct>,
      Promise<CreateUpdateEntries<CreateUpdateProductCommissionsSettings>>,
    ] = isTopAgentPlus
      ? [basePromise, productCommissionsSettingsForm.validateFields()]
      : [basePromise, createUpdateProductCommissionsSettingsPromise];

    try {
      const results = await Promise.all(productFormsValidationPromises);

      const productFormValues = results[0];
      const productCommissionsSettingsFormValues = results[1];

      let productData: Product;

      if (product) {
        productData = await updateProduct.mutateAsync(
          { id: product.id, object: productFormValues },
          {
            onSuccess: async (createdUpdatedProduct) => {
              if (isTopAgentPlus && !isEmptyOrUndefined(productCommissionsSettingsFormValues)) {
                await createUpdateProductCommissionsSettings.mutateAsync(
                  {
                    id: createdUpdatedProduct.id,
                    object: productCommissionsSettingsFormValues,
                  },
                  {
                    onSuccess: onFormCancel,
                    onError: (error) =>
                      setErrorsToForm(
                        productCommissionsSettingsForm,
                        "commissions.productCommissionsSettings.attrs",
                        error.violations,
                      ),
                  },
                );
              } else {
                onFormCancel();
              }

              queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProducts() });
            },
            onError: (error) => setErrorsToForm(productForm, "product.attrs", error.violations),
          },
        );
      } else {
        productData = await createProduct.mutateAsync(productFormValues, {
          onSuccess: async (createdUpdatedProduct) => {
            if (isTopAgentPlus && !isEmptyOrUndefined(productCommissionsSettingsFormValues)) {
              await createUpdateProductCommissionsSettings.mutateAsync(
                {
                  id: createdUpdatedProduct.id,
                  object: productCommissionsSettingsFormValues,
                },
                {
                  onSuccess: onFormCancel,
                  onError: (error) =>
                    setErrorsToForm(
                      productCommissionsSettingsForm,
                      "commissions.productCommissionsSettings.attrs",
                      error.violations,
                    ),
                },
              );
            } else {
              onFormCancel();
            }

            queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProducts() });
          },
          onError: (error) => setErrorsToForm(productForm, "product.attrs", error.violations),
        });
      }

      productForm.setFieldValue("optimisticLockVersion", productData.optimisticLockVersion);
    } catch {
      notification.error({
        title: t("common.error"),
        description: t(
          isTopAgentPlus
            ? "product.helpers.productWithCommissionsSettingsFormErrors"
            : "product.helpers.productFormErrors",
        ),
      });
    }
  };

  return (
    <Modal
      width={ModalSizes.HUGE}
      open={open}
      title={t(product ? "product.titles.updateProduct" : "product.titles.createProduct")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createProduct.isPending || updateProduct.isPending}
      afterClose={() => {
        productForm.resetFields();

        if (isTopAgentPlus) {
          productCommissionsSettingsForm.resetFields();
        }

        setIsCoveredByNbsConfirmOpen(false);
      }}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      destroyOnHidden
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={product} />

      <Form form={productForm} layout="vertical" name="productForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t("product.helpers.productName")}
              rules={[validations.notBlank, validations.size(1, 1024)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <ProductGroupSelect
              formItemProps={{
                name: "productGroupId",
                label: t("product.attrs.productGroupId"),
                rules: [validations.notNull],
              }}
            />
          </Col>

          <Col span={colSpan}>
            <Form.Item name="sector" label={t("product.enums.financialSector._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                disabled={!!product}
                options={Object.keys(ProductFinancialSector)
                  .filter((sector) => sector !== ProductFinancialSector.ALL)
                  .map((sector) => ({
                    value: sector,
                    label: t("product.enums.financialSector." + sector),
                  }))}
                onChange={handleSectorChange}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="deactivated"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
              className="form-item-without-label"
            >
              <Checkbox>{t("product.attrs.deactivated")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {sector === ProductFinancialSector.NON_LIFE_INSURANCES ||
        sector === ProductFinancialSector.LIFE_INSURANCES ||
        sector === ProductFinancialSector.CAPITAL_MARKET ||
        sector === ProductFinancialSector.SENIOR_PENSION_SAVINGS ||
        sector === ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS ? (
          <Row gutter={rowGutter}>
            {(sector === ProductFinancialSector.NON_LIFE_INSURANCES ||
              sector === ProductFinancialSector.LIFE_INSURANCES) && (
              <Col span={colSpan}>
                <Form.Item
                  name="insuranceType"
                  label={t("product.helpers.insuranceTypeLabel")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    disabled={product?.insuranceType === InsuranceType.LIFE}
                    options={Object.keys(InsuranceType).map((type) => ({
                      value: type,
                      label: t("contract.enums.insuranceType." + type),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={colSpan}>
              <Form.Item
                name="externalPartnerCode"
                label={t("product.attrs.externalPartnerCode")}
                rules={[validations.size(1, 64)]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        ) : undefined}

        {insuranceType === InsuranceType.LIFE ? (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item name="tariffGroupId" label={t("product.attrs.tariffGroupId")} rules={[validations.notNull]}>
                <LifeInsuranceTariffGroupSelect disabled={!!product?.tariffGroup} />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="eucsAssignable"
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
                className="form-item-without-label"
              >
                <Checkbox>{t("product.attrs.eucsAssignable")}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        ) : undefined}

        {sector === ProductFinancialSector.CAPITAL_MARKET ? (
          <Row gutter={rowGutter}>
            <Col span={colSpan}>
              <Form.Item
                name="eicContractNumberFirstDigit"
                label={t("product.attrs.eicContractNumberFirstDigit")}
                rules={[validations.minNumber(1), validations.maxNumber(9)]}
              >
                <InputNumber />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="wemContractNumberLastTwoDigits"
                label={t("product.attrs.wemContractNumberLastTwoDigits")}
                rules={[validations.size(2, 2), validations.numeric]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <Form.Item
                name="iadOneTimeInvestmentContract"
                valuePropName="checked"
                rules={[validations.none]}
                initialValue={false}
                className="form-item-without-label"
              >
                <Checkbox>{t("product.attrs.iadOneTimeInvestmentContract")}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        ) : undefined}

        <Row gutter={rowGutter}>
          <Col span={16}>
            <Flex style={{ height: 32 }} align="center">
              <label style={{ marginRight: 8 }} className="form-item-required">
                {t("product.attrs.coveredByNbs")}:
              </label>

              <HiddenInput name="coveredByNbs" initialValue={true} />

              <Popconfirm
                styles={{ root: { width: 310 } }}
                title={t("product.helpers.changeCoveredByNbsConfirm")}
                cancelText={t("common.no")}
                okText={t("common.yes")}
                onConfirm={() => {
                  setIsCoveredByNbsConfirmOpen(false);
                  productForm.setFieldsValue({ coveredByNbs: false });
                }}
                onCancel={() => {
                  setIsCoveredByNbsConfirmOpen(false);
                }}
                open={isCoveredByNbsConfirmOpen}
              >
                <Segmented<boolean>
                  value={coveredByNbs}
                  size="small"
                  options={[
                    {
                      value: true,
                      label: isTopAgentPlus
                        ? t("product.helpers.productProfiPlus")
                        : t("product.helpers.productCoveredByNbs"),
                    },
                    {
                      value: false,
                      label: isTopAgentPlus
                        ? t("product.helpers.productProjectsPlus")
                        : t("product.helpers.productNoCoveredByNbs"),
                    },
                  ]}
                  onChange={(value) => {
                    if (!isTopAgentPlus) {
                      productForm.setFieldsValue({ coveredByNbs: value });
                      return;
                    }

                    if (!value) {
                      setIsCoveredByNbsConfirmOpen(true);
                    } else {
                      productForm.setFieldsValue({ coveredByNbs: true });
                    }
                  }}
                />
              </Popconfirm>
            </Flex>
          </Col>
          <Col span={colSpan}>
            <Form.Item name="subjectToVat" valuePropName="checked" rules={[validations.notNull]} initialValue={false}>
              <Checkbox>{t("product.attrs.subjectToVat")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={colSpan * 3}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionIds",
                label: t("product.attrs.institutionIds"),
                rules: [validations.notNull],
              }}
              selectProps={{ mode: "multiple", maxTagCount: "responsive" }}
              optionsProps={{
                selected: product?.institutions,
                filterType: PRODUCT_SECTOR_TO_INSTITUTION_TYPE_MAP.get(sector),
              }}
            />
          </Col>
        </Row>

        {sector === ProductFinancialSector.OTHERS && (
          <Card size="small" title="IBIS">
            <Row gutter={rowGutter}>
              <Col span={colSpan}>
                <Form.Item
                  name="ibisProductIds"
                  label={t("product.attrs.ibisProductIds")}
                  rules={[validations.size(1, 255)]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={colSpan}>
                <Form.Item
                  name="ibisCommodityWeight"
                  label={t("product.attrs.ibisCommodityWeight")}
                  rules={[ibisProductIds ? validations.notNull : validations.none]}
                >
                  <InputNumber type="decimal" addon="g" />
                </Form.Item>
              </Col>

              <Col span={colSpan}>
                <Form.Item
                  name="ibisCommodityShape"
                  label={t("contract.enums.commodityShape._label")}
                  rules={[ibisProductIds ? validations.notNull : validations.none]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.values(CommodityShape).map((type) => ({
                      value: type,
                      label: t(`contract.enums.commodityShape.${type}`),
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}
      </Form>

      <ComponentWithPermission permissions={[Permission.COMMISSIONS_MANAGE]}>
        {isTopAgentPlus ? (
          <>
            <ProductCommissionsSettingsForm form={productCommissionsSettingsForm} coveredByNbs={coveredByNbs} />
          </>
        ) : undefined}
      </ComponentWithPermission>
    </Modal>
  );
};
